import { useState, useCallback, type ReactNode } from 'react';
import { useScrollIndicator } from '../../hooks/useScrollIndicator';
import { ScrollMoreHint } from '../common/ScrollMoreHint';
import type { AnswerResult, SessionOrigin } from '../../types/problems';
import { SESSION_ORIGIN_CONFIG } from '../../types/problems';
import { problemsApi } from '../../api/problemsApi';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { renderWithEditorNote } from '../../utils/editorNote';

// Format a single bid: suit letters uppercase, Pass/Dbl/Rdbl proper case
function formatBid(bid: string): string {
  const trimmed = bid.trim();
  const upper = trimmed.toUpperCase();

  // Handle special bids
  if (upper === 'PASS' || upper === 'P') return 'Pass';
  if (upper === 'DBL' || upper === 'DOUBLE' || upper === 'X') return 'Dbl';
  if (upper === 'RDBL' || upper === 'REDOUBLE' || upper === 'XX') return 'Rdbl';

  // For regular bids, ensure suit letters are uppercase (e.g., "1c" → "1C", "3nt" → "3NT")
  return upper;
}

// Format answer with multiple options: "3NT/4H" → "3NT or 4H"
function formatAnswer(answer: string): string {
  return answer.split('/').map(opt => formatBid(opt)).join(' or ');
}

// Check if answer has multiple options
function hasMultipleAnswers(answer: string): boolean {
  return answer.includes('/');
}

// Format user bid display: X → Dbl, XX → Rdbl
function formatUserBid(bid: string): string {
  return formatBid(bid);
}

// ============================================================
// ResultInfoBox - Compact result display (Correct/Incorrect, answer, you bid)
// Displayed in place of BidInput when showing results
// Uses CSS media queries for responsive sizing (no JS viewport detection)
// ============================================================
interface ResultInfoBoxProps {
  result: AnswerResult | null;
  wasSkipped: boolean;
  userBid?: string | null;
}

export function ResultInfoBox({ result, wasSkipped, userBid }: ResultInfoBoxProps) {
  if (wasSkipped) {
    return (
      <div className="bg-cream border border-line rounded-lg p-3 short:p-2">
        <div className="text-base short:text-sm font-bold text-ink-soft">
          Skipped
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div
      className={`border rounded-lg p-3 short:p-2 space-y-1 short:space-y-0.5 ${
        result.isCorrect
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
    >
      <div
        className={`text-lg short:text-base font-bold ${
          result.isCorrect ? 'text-green-700' : 'text-red-700'
        }`}
      >
        {result.isCorrect ? 'Correct!' : 'Incorrect!'}
      </div>

      {/* Show Answer: always for incorrect, only for correct when multiple answers */}
      {(!result.isCorrect || hasMultipleAnswers(result.correctBid)) && (
        <div className="text-sm short:text-xs text-ink">
          <span className="font-medium">Answer:</span>{' '}
          <span className="font-bold">{formatAnswer(result.correctBid)}</span>
        </div>
      )}

      {userBid && (
        <div className="text-sm short:text-xs text-ink-soft">
          You bid: <span className="font-medium text-ink">{formatUserBid(userBid)}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ExpertFeedbackSection - Combined Result Feedback + Jeff Says + action buttons
// v2.2: Displayed at bottom as a single container with all result information
// Uses CSS media queries for responsive sizing (no JS viewport detection)
// ============================================================
interface ExpertFeedbackSectionProps {
  result: AnswerResult | null;
  wasSkipped: boolean;
  onNext: () => void;
  problemId?: number;
  sessionOrigin?: SessionOrigin | null;
  onRemoveFromReview?: (problemId: number) => void;
  userBid?: string | null;
  buttonsOnly?: boolean;
  containerWidth?: string;
  containerHeight?: string;
  /**
   * Extra controls for the `buttonsOnly` action row, rendered as siblings of "Next Problem"
   * (client request, 2026-07-30: the phone review pin sits BESIDE Next Problem rather than on
   * its own line beneath it — one button row instead of two saves ~50px of a viewport-locked
   * decision panel).
   *
   * A slot rather than a feature: the pin's state and gating stay with the page that owns them
   * (useAddToReview), and this component keeps knowing only about its own two buttons.
   */
  extraActions?: ReactNode;
  /**
   * Controls for the `buttonsOnly` row that belong BEFORE "Next Problem" — the "Previous"
   * step-back (client request, 2026-08-03). Separate from `extraActions` purely for order:
   * back-then-forward is the way every pager on the site reads, and a Previous that trailed
   * Next would be the one that does not.
   */
  leadingActions?: ReactNode;
}

export function ExpertFeedbackSection({
  result,
  wasSkipped,
  onNext,
  problemId,
  sessionOrigin,
  onRemoveFromReview,
  userBid,
  buttonsOnly = false,
  containerWidth,
  containerHeight,
  extraActions,
  leadingActions,
}: ExpertFeedbackSectionProps) {
  const [isRemovingFromReview, setIsRemovingFromReview] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const modeConfig = sessionOrigin ? SESSION_ORIGIN_CONFIG[sessionOrigin] : null;

  // Scroll-more chevron indicator for expert comment overflow (iOS Safari hint)
  const { ref: commentScrollRef, showHint: showCommentHint } = useScrollIndicator();

  const handleRemoveClick = useCallback(() => {
    setShowRemoveConfirm(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!problemId || isRemovingFromReview || !onRemoveFromReview) return;
    setIsRemovingFromReview(true);
    setShowRemoveConfirm(false);
    try {
      await onRemoveFromReview(problemId);
    } finally {
      setIsRemovingFromReview(false);
    }
  }, [problemId, isRemovingFromReview, onRemoveFromReview]);

  const handleCancelRemove = useCallback(() => {
    setShowRemoveConfirm(false);
  }, []);

  // Render only buttons if buttonsOnly is true
  if (buttonsOnly) {
    return (
      // `verdict-actions` is the CSS hook the shortest landscape band tightens this row by
      // (index.css) — at 303px of viewport the two buttons wrap onto a second line and the gap
      // between them is real height.
      <div className="verdict-actions flex flex-wrap items-center gap-2">
        {leadingActions}

        <button
          type="button"
          onClick={onNext}
          className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700"
        >
          Next Problem
        </button>

        {/* Add to Review is NOT owned here (feature 023): the pin lives in the status line under
            the frame, as the reference design draws it, with a phone counterpart the PAGE renders
            and passes in through `extraActions` — one control per action per viewport, and one
            owner of its state (useAddToReview). It shares this row so the two controls sit side by
            side (client request, 2026-07-30). Removing from review stays: it is review mode's own
            action, has a confirmation to own, and never coexists with the pin. */}
        {extraActions}

        {modeConfig?.showRemoveFromReview && problemId && (
          <button
            type="button"
            onClick={handleRemoveClick}
            disabled={isRemovingFromReview}
            className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-danger text-white hover:bg-danger-hover"
          >
            {isRemovingFromReview ? 'Removing...' : 'Remove from Review'}
          </button>
        )}

        <ConfirmDialog
          isOpen={showRemoveConfirm}
          title="Remove from Review?"
          message="Are you sure you want to remove this problem from your review list?"
          confirmLabel="Remove"
          cancelLabel="Cancel"
          confirmVariant="danger"
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
        />
      </div>
    );
  }

  // Skipped state - simple message
  if (wasSkipped) {
    const skippedStyle: React.CSSProperties = {};
    if (containerWidth) skippedStyle.width = containerWidth;
    if (containerHeight) skippedStyle.height = containerHeight;
    return (
      <div
        className="bg-cream border border-line rounded-lg p-3"
        style={Object.keys(skippedStyle).length > 0 ? skippedStyle : undefined}
      >
        <div className="font-bold text-ink-soft text-base short:text-sm">
          Skipped
        </div>
      </div>
    );
  }

  if (!result) return null;

  // Build container style with width and height
  const containerStyle: React.CSSProperties = {};
  if (containerWidth) containerStyle.width = containerWidth;
  if (containerHeight) containerStyle.height = containerHeight;

  return (
    <div
      className={`border rounded-lg p-3 self-start narrow:self-auto overflow-hidden feedback-fixed-height ${
        result.isCorrect
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
      style={Object.keys(containerStyle).length > 0 ? containerStyle : undefined}
    >
      {/* Result header - Correct/Incorrect */}
      <div className={`font-bold text-lg short:text-base ${
        result.isCorrect ? 'text-green-700' : 'text-red-700'
      }`}>
        {result.isCorrect ? 'Correct!' : 'Incorrect!'}
      </div>

      {/* Answer and You bid - displayed on same line */}
      <div className="flex flex-wrap gap-4 text-base short:text-sm mt-2 short:mt-1">
        {/* Show Answer: always for incorrect, only for correct when multiple answers */}
        {(!result.isCorrect || hasMultipleAnswers(result.correctBid)) && (
          <span className="text-ink">
            <span className="font-medium">Answer:</span>{' '}
            <span className="font-bold">{formatAnswer(result.correctBid)}</span>
          </span>
        )}
        {userBid && (
          <span className="text-ink-soft">
            <span className="font-medium">You bid:</span>{' '}
            <span className="font-semibold">{formatUserBid(userBid)}</span>
          </span>
        )}
      </div>

      {/* Jeff Says section - inside the same container */}
      {result.expertComment && (
        <div className="border-t mt-3 short:mt-1 pt-3 short:pt-1 border-line narrow:flex-1 narrow:min-h-0 narrow:flex narrow:flex-col feedback-jeff-says">
          <div className="font-bold text-ink text-base short:text-sm">
            {result.expertName} says:
          </div>
          {/* Expert comment with fixed height and scroll - FR-082: responsive max-height */}
          <div
            ref={commentScrollRef}
            className="mt-1 overflow-y-auto scrollable text-ink-soft expert-comment-height"
          >
            <p className="text-base short:text-sm">
              {renderWithEditorNote(result.expertComment)}
            </p>
            {showCommentHint && <ScrollMoreHint />}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ResultFeedback - Legacy component (kept for backwards compatibility)
// Use ResultInfoBox + ExpertFeedbackSection for split layout
// Uses CSS media queries for responsive sizing (no JS viewport detection)
// ============================================================
interface ResultFeedbackProps {
  result: AnswerResult | null;
  wasSkipped: boolean;
  onNext: () => void;
  userBid?: string | null;
  problemId?: number;
  sessionOrigin?: SessionOrigin | null;
  onRemoveFromReview?: (problemId: number) => void;
}

export function ResultFeedback({
  result,
  wasSkipped,
  onNext,
  userBid,
  problemId,
  sessionOrigin,
  onRemoveFromReview,
}: ResultFeedbackProps) {
  const [addedToReview, setAddedToReview] = useState(false);
  const [isAddingToReview, setIsAddingToReview] = useState(false);
  const [isRemovingFromReview, setIsRemovingFromReview] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const modeConfig = sessionOrigin ? SESSION_ORIGIN_CONFIG[sessionOrigin] : null;

  const handleAddToReview = useCallback(async () => {
    if (!problemId || isAddingToReview || addedToReview) return;
    setIsAddingToReview(true);
    try {
      await problemsApi.addToReview(problemId);
      setAddedToReview(true);
    } catch (err) {
      console.error('Failed to add to review:', err);
    } finally {
      setIsAddingToReview(false);
    }
  }, [problemId, isAddingToReview, addedToReview]);

  const handleRemoveClick = useCallback(() => {
    setShowRemoveConfirm(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!problemId || isRemovingFromReview || !onRemoveFromReview) return;
    setIsRemovingFromReview(true);
    setShowRemoveConfirm(false);
    try {
      await onRemoveFromReview(problemId);
    } finally {
      setIsRemovingFromReview(false);
    }
  }, [problemId, isRemovingFromReview, onRemoveFromReview]);

  const handleCancelRemove = useCallback(() => {
    setShowRemoveConfirm(false);
  }, []);

  if (wasSkipped) {
    return (
      <div className="bg-cream border border-line rounded-lg p-6 short:p-2 space-y-4 short:space-y-1">
        <div className="text-xl short:text-base font-bold text-ink-soft">
          Time's up! Problem skipped.
        </div>
        <div className="flex flex-wrap gap-3 short:gap-1">
          <button
            type="button"
            onClick={onNext}
            className="px-5 py-2.5 short:px-2 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700"
          >
            Next Problem
          </button>
          {modeConfig?.allowAddToReview && problemId && (
            <button
              type="button"
              onClick={handleAddToReview}
              disabled={isAddingToReview || addedToReview}
              className={`px-5 py-2.5 short:px-2 short:py-1 short:text-sm rounded-md font-medium ${
                addedToReview
                  ? 'bg-paper text-ink-soft cursor-not-allowed'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
              }`}
            >
              {isAddingToReview ? 'Adding...' : addedToReview ? 'Added' : 'Add to Review'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div
      className={`border rounded-lg p-6 short:p-2 space-y-4 short:space-y-1 ${
        result.isCorrect
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      }`}
    >
      <div
        className={`text-2xl short:text-lg font-bold ${
          result.isCorrect ? 'text-green-700' : 'text-red-700'
        }`}
      >
        {result.isCorrect ? 'Correct!!' : 'Incorrect!'}
      </div>

      {/* Show Answer: always for incorrect, only for correct when multiple answers */}
      {(!result.isCorrect || hasMultipleAnswers(result.correctBid)) && (
        <div className="text-ink short:text-sm">
          <span className="font-medium">Answer:</span>{' '}
          <span className="font-bold text-lg short:text-base">{formatAnswer(result.correctBid)}</span>
        </div>
      )}

      {userBid && (
        <div className="flex items-baseline gap-2 short:gap-1">
          <span className="text-sm short:text-xs text-ink-soft">You bid:</span>
          <span className="font-medium text-ink short:text-sm">{formatUserBid(userBid)}</span>
        </div>
      )}

      {result.expertComment && (
        <div className="border-t pt-4 mt-4 short:pt-1 short:mt-1">
          <h4 className="font-bold text-ink text-lg short:text-sm mb-2 short:mb-0.5">
            {result.expertName} says:
          </h4>
          <p className="text-ink-soft short:text-xs">{renderWithEditorNote(result.expertComment)}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 short:gap-1">
        <button
          type="button"
          onClick={onNext}
          className="px-5 py-2.5 short:px-2 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700"
        >
          Next Problem
        </button>

        {modeConfig?.allowAddToReview && problemId && (
          <button
            type="button"
            onClick={handleAddToReview}
            disabled={isAddingToReview || addedToReview}
            className={`px-5 py-2.5 short:px-2 short:py-1 short:text-sm rounded-md font-medium ${
              addedToReview
                ? 'bg-paper text-ink-soft cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
          >
            {isAddingToReview ? 'Adding...' : addedToReview ? 'Added' : 'Add to Review'}
          </button>
        )}

        {modeConfig?.showRemoveFromReview && problemId && (
          <button
            type="button"
            onClick={handleRemoveClick}
            disabled={isRemovingFromReview}
            className="px-5 py-2.5 short:px-2 short:py-1 short:text-sm rounded-md font-medium bg-red-500 text-white hover:bg-red-600"
          >
            {isRemovingFromReview ? 'Removing...' : 'Remove from Review'}
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={showRemoveConfirm}
        title="Remove from Review?"
        message="Are you sure you want to remove this problem from your review list?"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </div>
  );
}
